import React, { useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Flex,
} from '@chakra-ui/react';
import { FaUsers, FaUserPlus, FaLock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import ThemeContext from '../../context/ThemeContext';

function SettingsEmployeeManagementFree() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { themeData } = useContext(ThemeContext);

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const accentColor = themeData?.colors?.accent || '#63B3ED';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';

  // Theme-aware colors
  const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
  const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
  const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.700';
  // const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
  const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
  const rowBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';
  const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
  const overlayBg = isDarkTheme ? 'rgba(26, 32, 44, 0.92)' : 'rgba(255, 255, 255, 0.92)';
  const dashedBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.5)' : primaryColor;
  const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
  const ghostHoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : 'rgba(99, 179, 237, 0.2)';

  return (
    <Container maxW="container.xl" py={10} position="relative">
      {/* Lock Overlay */}
      <Flex
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={5}
        bg={overlayBg}
        backdropFilter="blur(3px)"
        borderRadius="xl"
        justify="center"
        align="center"
        flexDirection="column"
        textAlign="center"
        border="3px dashed"
        borderColor={dashedBorderColor}
      >
        <Icon 
          as={FaLock} 
          boxSize={16} 
          color={isDarkTheme ? accentColor : primaryColor} 
          mb={4} 
        />
        <Heading size="xl" mb={3} color={headingTextColor}>
          Role Management & Creation
        </Heading>
        <Text color={strongTextColor} maxW="md" mb={6} fontSize="lg" fontWeight="medium">
          Unlock advanced employee management, role controls, and productivity insights with FinanSee Premium.
        </Text>
        <Button
          size="lg"
          bgGradient={`linear(to-r, ${primaryColor}, ${accentColor})`}
          color="white"
          onClick={() => navigate('/subscription')}
          mb={3}
          px={8}
          py={6}
          fontSize="lg"
          _hover={{ bgGradient: `linear(to-r, ${secondaryColor}, ${primaryColor})` }}
        >
          Upgrade Now
        </Button>
        <Button 
          variant="ghost" 
          color={isDarkTheme ? accentColor : primaryColor}
          onClick={onOpen} 
          size="md"
          _hover={{ bg: ghostHoverBg }}
        >
          What's Included?
        </Button>
      </Flex>

      {/* Blurred SettingsPaid Scaffolding - Much More Visible */}
      <Box filter="blur(1.5px)" opacity={0.6} pointerEvents="none">
        <VStack spacing={10} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading
              size="xl"
              bgGradient={isDarkTheme 
                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                : `linear(to-r, ${primaryColor}, ${accentColor})`
              }
              bgClip="text"
              mb={2}
            >
              Employee Management
            </Heading>
            <Text color={bodyTextColor} fontSize="md">
              Create and manage Relationship Managers for your organization
            </Text>
          </Box>

          {/* Action Button Section */}
          <Flex
            justify="space-between"
            align="center"
            bgGradient={isDarkTheme 
              ? `linear(to-r, ${accentColor}, ${primaryColor})` 
              : `linear(to-r, ${primaryColor}, ${accentColor})`
            }
            color="white"
            p={6}
            borderRadius="xl"
            boxShadow="xl"
          >
            <HStack spacing={3}>
              <Icon as={FaUsers} boxSize={7} />
              <Box>
                <Text fontSize="sm" opacity={0.9}>
                  Total Employees
                </Text>
                <Heading size="lg">3</Heading>
              </Box>
            </HStack>

            <Button
              leftIcon={<FaUserPlus />}
              variant="solid"
              colorScheme="blackAlpha"
              size="lg"
              color="white"
            >
              Add Relationship Manager
            </Button>
          </Flex>

          {/* Employee List Section */}
          <Card 
            borderRadius="2xl" 
            boxShadow="xl" 
            overflow="hidden" 
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <CardBody p={0}>
              <Box
                bgGradient={isDarkTheme 
                  ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                  : `linear(to-r, ${primaryColor}, ${accentColor})`
                }
                color="white"
                p={6}
              >
                <Heading size="md">
                  <HStack spacing={2}>
                    <Icon as={FaUsers} />
                    <Text>Role Directory</Text>
                  </HStack>
                </Heading>
              </Box>

              <Box p={6}>
                <VStack spacing={4} align="stretch">
                  {/* Sample Employee Rows */}
                  <Flex 
                    justify="space-between" 
                    align="center" 
                    p={4} 
                    bg={rowBg} 
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Box>
                      <Text fontWeight="bold" fontSize="md" color={strongTextColor}>John Smith</Text>
                      <Text fontSize="sm" color={bodyTextColor}>john.smith@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg={isDarkTheme ? accentColor : primaryColor}
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>

                  <Flex 
                    justify="space-between" 
                    align="center" 
                    p={4} 
                    bg={rowBg} 
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Box>
                      <Text fontWeight="bold" fontSize="md" color={strongTextColor}>Sarah Johnson</Text>
                      <Text fontSize="sm" color={bodyTextColor}>sarah.j@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg={isDarkTheme ? accentColor : primaryColor}
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>

                  <Flex 
                    justify="space-between" 
                    align="center" 
                    p={4} 
                    bg={rowBg} 
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                  >
                    <Box>
                      <Text fontWeight="bold" fontSize="md" color={strongTextColor}>Michael Chen</Text>
                      <Text fontSize="sm" color={bodyTextColor}>m.chen@company.com</Text>
                    </Box>
                    <Box
                      px={4}
                      py={1}
                      bg={isDarkTheme ? accentColor : primaryColor}
                      color="white"
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      RM
                    </Box>
                  </Flex>
                </VStack>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      {/* Popup Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay 
          backdropFilter="blur(8px)" 
          bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'}
        />
        <ModalContent 
          borderRadius="xl" 
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
        >
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FaLock} color={isDarkTheme ? accentColor : primaryColor} />
              <Text color={headingTextColor}>What's Included in FinanSee Premium</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={bodyTextColor} />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Text color={bodyTextColor}>
                <Text as="span" fontWeight="bold" color={strongTextColor}>•</Text> Add and manage unlimited employees (Relationship Managers)
              </Text>
              <Text color={bodyTextColor}>
                <Text as="span" fontWeight="bold" color={strongTextColor}>•</Text> Role-based permissions and secure access controls
              </Text>
              <Text color={bodyTextColor}>
                <Text as="span" fontWeight="bold" color={strongTextColor}>•</Text> Full, unlimited performance and productivity insights
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              bgGradient={`linear(to-r, ${primaryColor}, ${accentColor})`}
              color="white"
              _hover={{ bg: buttonHoverBg }}
              onClick={() => {
                onClose();
                navigate('/subscription');
              }}
            >
              Upgrade to Premium
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default SettingsEmployeeManagementFree;